import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, ShieldAlert, X } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { useFacility } from '../context/FacilityContext';
import { formatDateTime } from '../utils/datetime';

// Tenant Impersonation — a tab of Users & Roles since 2026-08-06 (owner:
// one people-and-access surface); renders embedded there. The standalone
// /settings/impersonation route redirects in.
// Two lists: currently-active sessions (with force-exit) and the past
// 30 days of session history. Read-only otherwise; the actual mint
// action lives on the customer detail page ("View as tenant" button).
const Impersonation = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { currentFacility } = useFacility();
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exiting, setExiting] = useState({});

  const facilityId = currentFacility?._id;

  const fetchAll = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const [a, h] = await Promise.all([
        apiClient.get(`/api/facilities/${facilityId}/impersonation/active`),
        apiClient.get(`/api/facilities/${facilityId}/impersonation/history?limit=50`),
      ]);
      setActive(Array.isArray(a.data) ? a.data : []);
      setHistory(Array.isArray(h.data) ? h.data : []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not load impersonation sessions.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const forceExit = async (sessionId) => {
    if (!facilityId) return;
    if (!window.confirm('Force-exit this impersonation session?')) return;
    setExiting((s) => ({ ...s, [sessionId]: true }));
    try {
      await apiClient.post(`/api/facilities/${facilityId}/impersonation/${sessionId}/exit`);
      await fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Could not exit session.');
    } finally {
      setExiting((s) => ({ ...s, [sessionId]: false }));
    }
  };

  const fmt = (iso) => formatDateTime(iso) || '-';

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          {!embedded && (
            <>
              <button
                type="button"
                onClick={() => navigate('/settings/users?tab=impersonation')}
                className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-700 mb-2"
              >
                <ArrowLeft className="h-3 w-3" /> Back to Users &amp; Roles
              </button>
              <h1 className="text-xl font-semibold text-slate-900">Tenant Impersonation</h1>
            </>
          )}
          <p className="text-[13px] text-slate-400 mt-0.5">
            View a tenant's portal from their perspective to help with payment or navigation. All actions are audit-logged.
            Start a session from the customer record ("View as tenant").
          </p>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200/80 bg-rose-50 p-4 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 text-rose-600 mt-0.5" />
          <p className="text-[13px] text-rose-700">{error}</p>
        </div>
      )}

      <section className="mb-8">
        <h2 className="text-[15px] font-semibold text-slate-900 mb-2">
          Active sessions ({active.length})
        </h2>
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          {active.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-slate-400">
              No active impersonation sessions.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Operator</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Tenant</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Started</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Expires</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                  <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">&nbsp;</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {active.map((s) => (
                  <tr key={s.session_id}>
                    <td className="px-4 py-3 text-[13px] text-slate-700">{s.operator_email || '-'}</td>
                    <td className="px-4 py-3 text-[13px] text-slate-700">
                      <div>{s.customer_name || '-'}</div>
                      <div className="text-[11px] text-slate-400">{s.customer_email || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-500">{fmt(s.started_at)}</td>
                    <td className="px-4 py-3 text-[13px] text-slate-500">{fmt(s.expires_at)}</td>
                    <td className="px-4 py-3 text-[13px] text-slate-700">{s.actions_count}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => forceExit(s.session_id)}
                        disabled={exiting[s.session_id]}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-rose-200 text-[12px] text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      >
                        <X className="h-3 w-3" /> Force exit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-[15px] font-semibold text-slate-900 mb-2">
          Recent history
        </h2>
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          {history.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-slate-400">
              No impersonation activity yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Operator</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Tenant</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Started</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Ended</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {history.map((s) => (
                  <tr key={s.session_id}>
                    <td className="px-4 py-3 text-[13px] text-slate-700">{s.operator_email || '-'}</td>
                    <td className="px-4 py-3 text-[13px] text-slate-700">
                      <div>{s.customer_name || '-'}</div>
                      <div className="text-[11px] text-slate-400">{s.customer_email || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-500">{fmt(s.started_at)}</td>
                    <td className="px-4 py-3 text-[13px] text-slate-500">
                      {s.is_active
                        ? <span className="text-emerald-600 font-medium">active</span>
                        : fmt(s.exited_at)}
                      {s.exit_reason
                        ? <div className="text-[11px] text-slate-400">{s.exit_reason}</div>
                        : null}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-700">{s.actions_count}</td>
                    <td className="px-4 py-3 text-[13px] text-slate-500 truncate max-w-xs">{s.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Impersonation;
